import type {
  ObjectCategory,
  ObjectImportance,
  ObjectStatus,
  RecurringObject,
  RecurringObjectInput
} from '@/types';
import {
  normalizeRecurringObject
} from '@/utils/storyElementsHelpers';
import { BaseService } from './baseService';

export interface RecurringObjectValidationResult {
  valid: boolean;
  errors: string[];
}

const defaultStatus: ObjectStatus = 'active';
const defaultCategory: ObjectCategory = 'other';
const defaultImportance: ObjectImportance = 'major';

class RecurringObjectService extends BaseService<RecurringObject> {
  create(input: RecurringObjectInput): RecurringObject {
    const now = this.now();

    return normalizeRecurringObject({
      id: this.createId('object'),
      storyElementsId: input.storyElementsId,
      name: input.name,
      description: input.description,
      significance: input.significance,
      category: input.category ?? defaultCategory,
      importance: input.importance ?? defaultImportance,
      firstAppearance: input.firstAppearance ?? '',
      lastSeen: input.lastSeen ?? '',
      currentLocation: input.currentLocation ?? '',
      status: input.status ?? defaultStatus,
      characterId: input.characterId,
      locationId: input.locationId,
      uiState: { isExpanded: false },
      createdAt: now,
      updatedAt: now,
      notes: input.notes ?? ''
    });
  }

  validate(input: Partial<RecurringObjectInput>): RecurringObjectValidationResult {
    const errors: string[] = [];

    if (!input.name || input.name.trim().length === 0) {
      errors.push('Name is required.');
    }

    if (!input.description || input.description.trim().length < 20) {
      errors.push('Description must be at least 20 characters.');
    }

    if (input.description && input.description.length > 500) {
      errors.push('Description must be shorter than 500 characters.');
    }

    if (input.importance === 'critical' && (!input.significance || input.significance.trim().length === 0)) {
      errors.push('Critical objects require a defined significance.');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

export const recurringObjectService = new RecurringObjectService();
